/**
 * BlueprintDesigner Component
 *
 * This is the main entry point component for the RTA Blueprint design system.
 * It orchestrates the entire blueprint design environment, providing:
 * - Component controller management and lifecycle
 * - Event subscription and handling for kitchen design changes
 * - Context menu service integration
 * - Render tracking and performance monitoring
 * - Application context provider setup
 *
 * @author RTA Outdoor Living LLC
 * @copyright 2024 RTA Outdoor Living LLC. All rights reserved.
 */

import { useEffect, useState } from 'react';

import { type EventActionMessage, type EventCrudMessage } from '@rta/rta-blueprint-library';

import { AppRootContextProvider } from './app-root-context-provider';
import { ComponentController } from '@r-core';
import { contextMenuService } from '../context-menu';
import { Designer } from '../viewspace/designer';
import type { AnnotationPreset } from '../viewspace/sku-annotations/types';
import { RenderBlueprint } from '../viewspace/render';
import { RenderLayers } from '@r-services/render-layers';
import { renderTracking } from '@r-services/render-tracking';

import './styles/blueprint-component.scss';

/**
 * Props interface for the BlueprintDesigner component
 */
type RtaBlueprintDesignerProps = {
	/** Component controller that manages the blueprint CAD application state and logic */
	controller: ComponentController | null;
	/** Background color for the designer canvas (defaults to 'unset') */
	backgroundColor?: string;
	/** #354 -- saved annotation presets and the save/delete callbacks; react-ui owns fetching and the API calls */
	annotationPresets?: Array<AnnotationPreset>;
	onSaveAnnotationPreset?: (name: string, annotations: Array<Record<string, unknown>>) => void;
	onDeleteAnnotationPreset?: (presetId: string) => void;
	/** Fired after a blueprint PDF/PPTX download so the host can save the design the export just dated (#607). */
	onBlueprintExported?: () => void;
};

/**
 * BlueprintDesigner - Main Entry Point Component
 *
 * This component serves as the orchestrator for the entire blueprint design system.
 * It manages the component lifecycle, event subscriptions, and provides the
 * application context for all child components.
 *
 * Key Responsibilities:
 * - Initialize and manage the component controller
 * - Set up event subscriptions for kitchen design changes
 * - Configure context menu and render tracking services
 * - Provide application context to child components
 * - Handle component cleanup and resource management
 * - Display appropriate error states when controller or kitchen data is missing
 *
 * Architecture:
 * - AppRootContextProvider: Provides application-wide context and state
 * - Designer: Core rendering engine with viewport and coordinate management
 * - ShowBlueprint: Content rendering and display mode management
 *
 * Error Handling:
 * - Missing Controller: Displays error message when controller prop is null
 * - Missing Kitchen Data: Displays error message when controller.kitchen is missing
 *
 * @param props - Component configuration and controller
 * @returns React.ReactElement - The complete blueprint design environment or error state
 */
export const RtaBlueprintComponent = (props: RtaBlueprintDesignerProps): React.ReactElement => {
	const { controller, backgroundColor = 'unset', annotationPresets, onSaveAnnotationPreset, onDeleteAnnotationPreset, onBlueprintExported } = props;

	/**
	 * State for handling kitchen design events (CRUD operations and actions)
	 * Currently commented out but available for debugging and event handling
	 */
	const [_event, setEvent] = useState<EventCrudMessage | EventActionMessage>(null);

	// Track render performance for this component
	renderTracking.incrementRender('BlueprintDesigner');

	/**
	 * Effect: Configure render tracking based on developer insights setting
	 * Enables/disables performance monitoring based on user preferences
	 */
	useEffect(() => {
		renderTracking.setEnabled(controller?.options.showDeveloperInsights);
	}, [controller?.options.showDeveloperInsights]);

	/**
	 * Effect: Controller lifecycle management and event subscription setup
	 *
	 * This effect handles:
	 * - Controller initialization and setup
	 * - Event subscription registration for kitchen design changes
	 * - Context menu service configuration
	 * - Cleanup and resource management on unmount
	 *
	 * Event Subscriptions:
	 * - Kitchen events: Design changes, appliance modifications, etc.
	 * - Controller events: Navigation, view mode changes, etc.
	 * - Wildcard subscriptions ('*', '*') capture all events for comprehensive tracking
	 */
	// Controller-level subscription + service lifecycle. Keyed on the controller
	// (stable for the whole session), so the services toggle once and the controller
	// subscription — which also carries the `kitchen_version updated` event fired when
	// the active kitchen instance is swapped — lives for the controller's lifetime.
	useEffect(() => {
		if (!controller) return undefined;

		// Enable context menu service for user interactions
		contextMenuService.setEnabled(true);

		// Subscribe to all controller events (navigation, view modes, kitchen swaps, etc.)
		const controllerSubId = controller.subscribe('*', '*', setEvent);

		return () => {
			renderTracking.setEnabled(false);
			contextMenuService.setEnabled(false);
			controller.unsubscribe(controllerSubId);
		};
	}, [controller]);

	// Kitchen-level subscription, RE-BOUND whenever the controller swaps its active
	// Kitchen instance mid-session (draft creation on load, promoteDraft + repoint on
	// save). Keying on the kitchen identity is essential: without it this wildcard
	// subscription stays attached to the now-abandoned Kitchen object, so subsequent
	// deletes/adds/moves publish on the live kitchen while the overhead view (and any
	// other surface relying on this subscription) never re-renders — the deleted-item
	// -still-showing bug. A brand-new design keeps one Kitchen for its whole life, which
	// is why "starting from scratch" always worked.
	useEffect(() => {
		const kitchen = controller?.kitchen;
		if (!kitchen) return undefined;

		const kitchenSubId = kitchen.subscribe('*', '*', setEvent);

		return () => {
			kitchen.unsubscribe(kitchenSubId);
		};
	}, [controller, controller?.kitchen]);

	/**
	 * Event Handling Effect (Currently Commented Out)
	 *
	 * This effect would handle incoming events and provide debugging/logging capabilities.
	 * It's commented out to reduce console noise but can be enabled for development.
	 *
	 * Event Types:
	 * - EventCrudMessage: Create, Read, Update, Delete operations
	 * - EventActionMessage: User actions and system events
	 *
	 * Uncomment to enable event logging and debugging
	 */
	// useEffect(() => {
	// 	if (event) {
	// 		if ((event as EventCrudMessage).payload.new) {
	// 			const e = event as EventCrudMessage;
	// 			console.log(`controller (bp) --> event:${e.noun}:${e.action}`, e.payload.ref, e.payload.old, e.payload.new);
	// 		} else if ((event as EventActionMessage).payload.data) {
	// 			const e = event as EventCrudMessage;
	// 			console.log(`controller (bp) --> event:${e.noun}:${e.action}`, e.payload.ref, e.payload.data);
	// 		}
	// 	}
	// }, [event]);

	/**
	 * Error State Handling: Controller and Kitchen Data Validation
	 *
	 * Check for missing controller or kitchen data and display appropriate error messages.
	 * This provides graceful degradation and clear feedback to users about what's missing.
	 */
	if (!controller) {
		return (
			<div className="missing-controller-error">
				<div className="error-content">
					<h2>Missing Controller</h2>
					<p>The blueprint component requires a valid controller to function.</p>
					<p>Please ensure a controller is provided to the component.</p>
				</div>
			</div>
		);
	}

	if (!controller.kitchen) {
		return (
			<div className="missing-kitchen-error">
				<div className="error-content">
					<h2>Missing Kitchen Data</h2>
					<p>The controller is present but kitchen data is missing.</p>
					<p>Please ensure the controller has valid kitchen data.</p>
				</div>
			</div>
		);
	}

	/**
	 * Main Component Render
	 *
	 * Component Hierarchy:
	 * - AppRootContextProvider: Application-wide context and state management
	 *   - Designer: Core rendering engine with viewport and coordinate management
	 *     - ShowBlueprint: Content rendering and display mode management
	 *
	 * This structure provides:
	 * - Centralized state management through React Context
	 * - Sophisticated viewport and coordinate system handling
	 * - Render layer optimization for performance
	 * - Context-sensitive user interactions
	 */
	return (
		<>
			{/* Application Context Provider - Provides state and services to all child components */}
			<AppRootContextProvider controller={controller}>
				{/* Services Demo - Showcases services */}
				{/* <ServicesDemo /> */}
				{/* Designer Component - Core rendering engine with viewport management */}
				<Designer backgroundColor={backgroundColor} annotationPresets={annotationPresets} onSaveAnnotationPreset={onSaveAnnotationPreset} onDeleteAnnotationPreset={onDeleteAnnotationPreset} onBlueprintExported={onBlueprintExported}>
					{/* ShowBlueprint - Content rendering and display mode management */}
					<RenderBlueprint></RenderBlueprint>
					<RenderLayers/>
				</Designer>
			</AppRootContextProvider>
		</>
	);
};
